import { findParentNodeByKey } from '@/utils';
import { useMutation } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { Button, Checkbox, Col, Drawer, Flex, Form, Input, Row, Tree } from 'antd';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { cRole, uRole } from '@/apis/role';

/**
 * Types
 */
import type { Permission } from '@/constants/permissions';
import type { RoleDto } from '@/dtos/role.dto';
import type { DrawerProps, FormProps, TreeProps } from 'antd';

export type Action =
    | {
          update: (data: RoleDto) => void;
      }
    | undefined;

type Props = {
    onFinish?: (data: RoleDto) => void;
};

type State = {
    checkedKeys: string[];
    open: boolean;
    type: 'create' | 'update';
};

/**
 * Constants
 */
import { permissions } from '@/constants/permissions';

const ComponentCreateUpdate = forwardRef<Action, Props>((props, ref) => {
    /**
     * Hooks
     */
    const [form] = Form.useForm();

    const { t } = useTranslation(['common', 'roles']);

    /**
     * States
     */
    const [state, setState] = useSetState<State>({
        checkedKeys: [],
        open: false,
        type: 'create',
    });

    /**
     * Requests
     */
    const { isPending: pendingCRole, mutate: mutateCRole } = useMutation({
        mutationKey: ['cRole'],
        mutationFn: cRole,
        onSuccess: ({ data: { data } }) => {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    const { isPending: pendingURole, mutate: mutateURole } = useMutation({
        mutationKey: ['uRole'],
        mutationFn: uRole,
        onSuccess: ({ data: { data } }) => {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    /**
     * ChildrenProps
     */
    const drawerProps: DrawerProps = {
        closeIcon: false,
        extra: (
            <Flex gap={8}>
                <Button
                    onClick={() => {
                        setState({
                            open: false,
                        });
                    }}
                >
                    {t('cancel')}
                </Button>

                <Button loading={pendingCRole || pendingURole} type="primary" onClick={form.submit}>
                    {t('save')}
                </Button>
            </Flex>
        ),
        maskClosable: false,
        open: state.open,
        title: state.type === 'create' ? t('create') : t('edit'),
        width: 385,
        afterOpenChange: (open) => {
            if (open) return;

            form.resetFields();

            setState({
                checkedKeys: [],
            });
        },
        onClose: () => {
            setState({
                open: false,
            });
        },
    };

    const formProps: FormProps = {
        autoComplete: 'off',
        form,
        layout: 'vertical',
        onFinish: (values) => {
            values.permissions = state.checkedKeys;

            if (state.type === 'create') {
                return mutateCRole(values);
            }

            if (state.type === 'update') {
                return mutateURole(values);
            }
        },
    };

    const treeProps: TreeProps<Permission> = {
        checkable: true,
        checkedKeys: state.checkedKeys,
        checkStrictly: true,
        defaultExpandAll: true,
        selectable: false,
        treeData: permissions,
        onCheck: (_, { checked, node }) => {
            const newCheckedKeys = new Set(state.checkedKeys);

            // 更新当前节点
            const updateCurrentStatus = (node: Permission) => {
                newCheckedKeys[checked ? 'add' : 'delete'](node.key as string);

                if (node.children) {
                    node.children.forEach((child: Permission) => updateCurrentStatus(child));
                }
            };

            updateCurrentStatus(node);

            // 更新父级节点
            const updateParentStatus = (node: Permission) => {
                const parentNode = findParentNodeByKey(permissions, node.key as string) as Permission;

                if (parentNode) {
                    // 选中
                    if (checked) {
                        newCheckedKeys.add(parentNode.key as string);

                        return updateParentStatus(parentNode);
                    }

                    // 取消选中
                    const someChecked = parentNode.children?.some((child: Permission) => {
                        return newCheckedKeys.has(child.key as string);
                    });

                    if (parentNode.access || someChecked) {
                        return;
                    }

                    newCheckedKeys.delete(parentNode.key as string);

                    updateParentStatus(parentNode);
                }
            };

            updateParentStatus(node);

            setState({
                checkedKeys: [...newCheckedKeys],
            });
        },
    };

    /**
     * Handles
     */
    useImperativeHandle(ref, () => {
        return {
            update: (data: RoleDto) => {
                form.setFieldsValue(data);

                setState({
                    checkedKeys: data.permissions,
                    open: true,
                    type: 'update',
                });
            },
        };
    });

    return (
        <>
            <Button
                type="primary"
                onClick={() => {
                    setState({
                        open: true,
                        type: 'create',
                    });
                }}
            >
                {t('create')}
            </Button>

            <Drawer {...drawerProps}>
                <Form {...formProps}>
                    <Form.Item hidden name="id">
                        <Input />
                    </Form.Item>

                    <Form.Item hidden name="version">
                        <Input />
                    </Form.Item>

                    <Form.Item label={t('name')} name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item label={t('status')}>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                <Form.Item name="is_disabled" noStyle={true} valuePropName="checked">
                                    <Checkbox>{t('status_disabled')}</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item>

                    <Form.Item label={t('page.roles.permissions')}>
                        <Tree {...treeProps} />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
});

export default ComponentCreateUpdate;
